import { MatchingProfilePreviewDialog } from "@/components/users/user-details/matching-profile-preview-dialog";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { RoleBadge } from "@/components/users/role-badge.tsx";
import {
  User,
  useClearUserCache,
  useDevCleanupUser,
  useGetUserSupervisorAndChannel,
} from "@/lib/api/user.ts";
import { useSendMentorInvite } from "@/lib/api/mentor-profile.ts";
import { getMentorInviteBlockers } from "@/lib/mentor-profile/invite-readiness";
import { useMentorProfileEnabled } from "@/lib/hooks/use-mentor-profile";
import { useToast } from "@/components/ui/use-toast";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import {
  getMenteeDisplayPhoto,
  getRecordDisplayName,
  isFieldRedacted,
} from "@/lib/redacted-display";
import {
  getPrimaryRole,
  isMentorIdentity,
} from "@/lib/utils/system-roles";
import { getInitials } from "@/lib/utils.ts";
import { ChevronLeftIcon } from "@radix-ui/react-icons";
import { useRouter } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  Loader2,
  Mail,
  RotateCcw,
  Smartphone,
  Trash2,
} from "lucide-react";
import { useState } from "react";

interface HeaderProps {
  user: User;
}

export function Header({ user }: HeaderProps) {
  const router = useRouter();
  const ability = useAbility();
  const clearUserCacheMutation = useClearUserCache();
  const devCleanupUserMutation = useDevCleanupUser();
  const sendMentorInviteMutation = useSendMentorInvite();
  const mentorProfileEnabled = useMentorProfileEnabled();
  const { toast } = useToast();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showMatchingPreview, setShowMatchingPreview] = useState(false);
  const { name, redacted_fields } = user;
  const isNameRedacted = isFieldRedacted("name", redacted_fields, name);
  const displayName = getRecordDisplayName(user);
  const displayPhoto = getMenteeDisplayPhoto(user);
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const canClearUserCache =
    hasPrivilegedAccess && ability.can("update", "users");
  const canUpdateOnboardingStatus =
    hasPrivilegedAccess && ability.can("update", "users");
  const canManageAll = ability.can("manage", "all");
  const canViewMatchingProfile =
    mentorProfileEnabled &&
    isMentorIdentity(user.systemRoles) &&
    ability.can("read", "mentor-profile-reviews");

  const isNonProduction =
    import.meta.env.VITE_STYTCH_PUBLIC_ENV !== "production";
  const canResetUser = isNonProduction && canManageAll;

  const handleClearCache = () => {
    clearUserCacheMutation.mutate(user.id);
  };

  const canSendMentorInvite =
    mentorProfileEnabled &&
    canUpdateOnboardingStatus &&
    isMentorIdentity(user.systemRoles);
  const { data: supervisorAndChannel, isPending: supervisorPending } =
    useGetUserSupervisorAndChannel(user.id, canSendMentorInvite);

  const handleSendMentorInvite = () => {
    const blockers = getMentorInviteBlockers(user, {
      hasLeadMentor: Boolean(supervisorAndChannel?.supervisor),
    });
    if (blockers.length > 0) {
      toast({
        title:
          "Onboarding email cannot be sent until the following fields are filled:",
        description: (
          <ul className="mt-2 list-disc pl-4">
            {blockers.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ),
        variant: "destructive",
      });
      return;
    }

    sendMentorInviteMutation.mutate(
      { userId: user.id },
      {
        onSuccess: async ({ inviteUrl }) => {
          try {
            await navigator.clipboard.writeText(inviteUrl);
            toast({
              title: "Onboarding invite ready",
              description: "Invite link copied to clipboard.",
            });
          } catch {
            toast({
              title: "Onboarding invite ready",
              description: inviteUrl,
            });
          }
        },
        onError: () => {
          toast({
            title: "Couldn't generate invite",
            description: "Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleResetUser = async () => {
    devCleanupUserMutation.mutate(user.id, {
      onSuccess: () => {
        setShowResetDialog(false);
      },
    });
  };

  const handleNavigateToMentorDashboard = () => {
    router.navigate({
      to: "/mentor-data-dashboard",
      search: { mentorId: user.id },
    });
  };

  return (
    <div className="flex flex-col gap-3 border-b pb-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-row items-center gap-2">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => {
            router.history.back();
          }}
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </Button>

        <div className="flex min-w-0 items-center gap-2">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src={displayPhoto} alt={displayName} />
            <AvatarFallback className="text-xs">
              {isNameRedacted ? displayName : getInitials(displayName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-1">
            <div className="truncate font-bold">{displayName}</div>
            <div className="flex flex-row flex-wrap items-center gap-2">
              <RoleBadge
                role={getPrimaryRole(user.systemRoles)}
                size="sm"
              />
              <div className="text-xs font-normal italic leading-tight text-zinc-600">
                Started on {format(new Date(user.created_at), "MMM dd, yyyy")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-10 sm:pl-0">
        {isMentorIdentity(user.systemRoles) && (
          <Button
            variant="default"
            size="sm"
            onClick={handleNavigateToMentorDashboard}
            className="flex items-center justify-center gap-2 px-3 py-2 text-white"
            style={{
              borderRadius: "var(--radius-med, 10px)",
              background:
                "var(--Components-Colors-Buttons-btn-primary, #1C1C1C)",
            }}
          >
            View Performance
          </Button>
        )}
        {canViewMatchingProfile && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMatchingPreview(true)}
              className="flex items-center gap-2"
            >
              <Smartphone className="h-4 w-4" />
              View Matching Profile
            </Button>
            <MatchingProfilePreviewDialog
              mentorId={user.id}
              mentorLabel={displayName}
              open={showMatchingPreview}
              onOpenChange={setShowMatchingPreview}
            />
          </>
        )}
        {canSendMentorInvite && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleSendMentorInvite}
            disabled={
              sendMentorInviteMutation.isPending || supervisorPending
            }
            className="flex items-center gap-2"
          >
            <Mail className="h-4 w-4" />
            {sendMentorInviteMutation.isPending
              ? "Generating..."
              : "Send Onboarding Email"}
          </Button>
        )}
        {canClearUserCache && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearCache}
            disabled={clearUserCacheMutation.isPending}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {clearUserCacheMutation.isPending ? "Clearing..." : "Clear Cache"}
          </Button>
        )}
        {canResetUser && (
          <>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowResetDialog(true)}
              disabled={devCleanupUserMutation.isPending}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              {devCleanupUserMutation.isPending ? "Resetting..." : "Reset User"}
            </Button>
            <Dialog
              open={showResetDialog}
              onOpenChange={(open) => {
                if (!open) setShowResetDialog(false);
              }}
            >
              <DialogContent className="w-full max-w-lg p-0" hideCloseButton>
                <div className="p-4">
                  <DialogHeader>
                    <DialogTitle className="mb-1 text-2xl font-semibold">
                      Reset User Data
                    </DialogTitle>
                    <DialogDescription className="text-base">
                      This will delete all user data including mentorships,
                      messages, profile information, and mentor matching-profile
                      reviews. This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4 flex flex-row items-center gap-4">
                    <Button
                      className="w-full text-lg font-bold text-slate-900"
                      variant="outline"
                      onClick={() => setShowResetDialog(false)}
                      disabled={devCleanupUserMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="w-full text-lg font-bold text-white"
                      variant="destructive"
                      onClick={handleResetUser}
                      disabled={devCleanupUserMutation.isPending}
                    >
                      {devCleanupUserMutation.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Reset User
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
